'use client';

import { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { Comment } from '@/types/comment';
import { useCommentInfiniteScroll } from '../hooks/use-comment-infinite-scroll';
import { useCommentSection } from '../hooks/use-comment-section';
import { useEnteringCommentIds } from '../hooks/use-entering-comment-ids';
import {
  COMMENT_LIST_SORT,
  type CommentListSort,
  type PublicationKind,
} from '../types/publication-kind';
import { getPublicationCopy } from '../utils/publication-copy.util';
import { CommentItem } from './comment-item';

export interface CommentSectionProps {
  articleId?: number;
  postId?: number;
  chapterId?: string;
  paragraphId?: string;
  className?: string;
  commentTitle?: string;
  hideTitle?: boolean;
  publicationKind?: PublicationKind;
  highlightCommentId?: number | null;
  onCommentsChange?: () => void;
  onHasNextPageChange?: (hasNextPage: boolean) => void;
  isActive?: boolean;
}

export function CommentSection({
  articleId,
  postId,
  chapterId,
  paragraphId,
  className = '',
  commentTitle = 'Комментарии',
  hideTitle = false,
  publicationKind = 'comment',
  highlightCommentId = null,
  onCommentsChange,
  onHasNextPageChange,
  isActive = true,
}: CommentSectionProps) {
  const copy = getPublicationCopy(publicationKind);
  const [sort, setSort] = useState<CommentListSort>(COMMENT_LIST_SORT.newest);
  const {
    comments,
    loading,
    error,
    topLevelComments,
    replyingTo,
    expandedReplies,
    repliesLoading,
    loadedReplies,
    reactingComments,
    localReactions,
    openReactionPopovers,
    resolveReactionCount,
    resolveUserReaction,
    handleReply,
    handleCancelReply,
    handleLoadReplies,
    handleReplySubmit,
    handleReaction,
    setReactionPopoverOpen,
    retryFetch,
    loadMore,
    hasNextPage,
    totalDocs,
    reportComment,
    handleDeleteComment,
    handleUpdateComment,
  } = useCommentSection({
    articleId,
    postId,
    chapterId,
    paragraphId,
    sort: publicationKind === 'review' ? sort : undefined,
    onCommentsChange,
    onHasNextPageChange,
  });

  const isInitialLoading = loading && comments.length === 0;
  const hasInitialError = Boolean(error) && comments.length === 0;
  const enteringCommentIds = useEnteringCommentIds(topLevelComments);
  const { sentinelIndex, observeLoadMoreNode } = useCommentInfiniteScroll({
    isActive,
    hasNextPage,
    loading,
    error,
    itemCount: topLevelComments.length,
    loadMore,
  });

  useEffect(() => {
    if (!highlightCommentId || loading) return;
    const node = document.getElementById(`comment-${highlightCommentId}`);
    node?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [comments, highlightCommentId, loading]);

  const handleReport = useCallback(
    async (commentId: number, category: string, reason: string, details?: string) => {
      return reportComment(commentId, category, reason, details);
    },
    [reportComment],
  );

  const handleDelete = useCallback(
    async (commentId: number) => {
      return handleDeleteComment(commentId);
    },
    [handleDeleteComment],
  );

  const handleUpdate = useCallback(
    async (commentId: number, content: string) => {
      return handleUpdateComment(commentId, content);
    },
    [handleUpdateComment],
  );

  return (
    <div className={`space-y-6 ${className}`}>
      {!hideTitle && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-muted-foreground dark:text-[#898989]" />
            <div className="text-lg font-semibold text-foreground dark:text-white">
              {commentTitle} ({totalDocs || (Array.isArray(comments) ? comments.length : 0)})
            </div>
          </div>
          {publicationKind === 'review' && (
            <div
              className="inline-flex rounded-full bg-[#F2F3F7] p-0.5"
              role="group"
              aria-label="Сортировка отзывов"
            >
              {(
                [
                  [COMMENT_LIST_SORT.newest, 'Новые'],
                  [COMMENT_LIST_SORT.popular, 'Популярные'],
                ] as const
              ).map(([value, label]) => {
                const selected = sort === value;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setSort(value)}
                    className={`rounded-full px-3 py-1 text-sm ${
                      selected
                        ? 'bg-white font-medium text-[#1B1D22] shadow-sm'
                        : 'text-[#6E7384] hover:text-[#1B1D22]'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isInitialLoading && (
        <div className="text-center py-8">
          <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-muted-foreground dark:text-[#898989]" />
          <p className="text-muted-foreground dark:text-[#898989]">{copy.loading}</p>
        </div>
      )}

      {hasInitialError && (
        <div className="text-center py-8">
          <p className="text-red-500 dark:text-red-400 mb-3">{copy.loadError}</p>
          <Button
            variant="outline"
            onClick={retryFetch}
            className="text-muted-foreground dark:text-[#898989] border-border dark:border-[#3a3f47] hover:bg-muted dark:hover:bg-[#3a3f47] hover:text-foreground dark:hover:text-white"
          >
            Попробовать снова
          </Button>
        </div>
      )}

      {!isInitialLoading && !hasInitialError && (
        <div className="relative w-full max-w-full overflow-x-hidden">
          <div className="w-full max-w-full overflow-x-hidden scrollbar-thin scrollbar-thumb-muted dark:scrollbar-thumb-[#3a3f47] scrollbar-track-background dark:scrollbar-track-[#1B1D22]">
            <div className="w-full max-w-full space-y-6">
              {Array.isArray(topLevelComments) && topLevelComments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground dark:text-[#898989]">
                  <MessageCircle className="h-12 w-12 mx-auto mb-3 text-muted dark:text-[#3a3f47]" />
                  <p>{copy.empty}</p>
                </div>
              ) : (
                Array.isArray(topLevelComments) &&
                topLevelComments.map((comment: Comment, index: number) => (
                  <motion.div
                    key={comment.id}
                    ref={index === sentinelIndex ? observeLoadMoreNode : undefined}
                    initial={enteringCommentIds.has(comment.id) ? { opacity: 0, y: 16 } : false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                  >
                    <CommentItem
                      comment={comment}
                      articleId={articleId}
                      postId={postId}
                      replyingTo={replyingTo}
                      expandedReplies={expandedReplies}
                      repliesLoading={repliesLoading}
                      loadedReplies={loadedReplies}
                      reactingComments={reactingComments}
                      localReactions={localReactions}
                      openReactionPopovers={openReactionPopovers}
                      resolveReactionCount={resolveReactionCount}
                      resolveUserReaction={resolveUserReaction}
                      onReply={handleReply}
                      onCancelReply={handleCancelReply}
                      onLoadReplies={handleLoadReplies}
                      onReplySubmit={handleReplySubmit}
                      onReaction={handleReaction}
                      onReactionPopoverOpenChange={setReactionPopoverOpen}
                      onReport={handleReport}
                      onDelete={handleDelete}
                      onUpdate={handleUpdate}
                      publicationKind={publicationKind}
                    />
                  </motion.div>
                ))
              )}

              {hasNextPage && (
                <div className="flex min-h-16 justify-center py-4">
                  {loading && (
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground dark:text-[#898989]" />
                  )}
                </div>
              )}

              {error && comments.length > 0 && (
                <div className="flex flex-col items-center gap-3 py-2">
                  <p className="text-red-500 dark:text-red-400">
                    {publicationKind === 'review'
                      ? 'Не удалось загрузить ещё отзывы'
                      : 'Не удалось загрузить ещё комментарии'}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => void loadMore()}
                    className="text-muted-foreground dark:text-[#898989] border-border dark:border-[#3a3f47] hover:bg-muted dark:hover:bg-[#3a3f47] hover:text-foreground dark:hover:text-white"
                  >
                    Попробовать снова
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
